// @flow
import React, { Component } from 'react';
import Dialog from 'material-ui/Dialog';
import RaisedButton from 'material-ui/RaisedButton';
import TextField from 'material-ui/TextField';
import styled, { withTheme } from 'styled-components';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { translate } from 'react-i18next';
import moment from 'moment';
import DateRangeIcon from 'material-ui/svg-icons/action/date-range';
import DateRangePicker from 'common/components/DateRangePicker';
import LoadingSpinner from 'common/components/LoadingSpinner';
import type { SalesPromotionParameters } from 'common/modules/sales/domain/SalesPromotionParameters';
import { PARSED_DATE_FORMAT } from 'common/modules/sales/domain/SalesPromotion';
import { openSnackbar } from 'common/modules/ui/actions/uiActions';
import CampaignListing from 'common/modules/campaign/domain/CampaignListing';
import CampaignsDropdown from 'common/components/CampaignsDropdown';
import type { FetchCampaignOptions } from 'common/modules/campaign/domain/Campaign';
import CampaignAPI from 'statsradio/modules/campaigns/services/CampaignAPI';
import { FETCH_ALL_CAMPAIGNS } from 'common/modules/campaign/domain/Campaign';
import { getCurrentRadio, getToken } from 'statsradio/modules/authentication/session/selectors/sessionSelectors';
import Radio from 'common/domain/radio';

import DatePickerWithInput from '../../components/DatePickerWithInput';
import NumericTextField from '../../components/NumericTextField';
import { createNewPromotion } from './actions/promotionActions';
import { getPromotionState } from './selectors/promotionSelectors';
import type { PromotionState } from './reducers/promotionReducer';
import DialogFooter from '../../components/DialogFooter';
import MonetaryTextField from '../../components/MonetaryTextField';


const Wrapper = styled.div`
  margin: ${({ theme }) => theme.metrics.largeMargin}px;
`;

const StyledLabel = styled.div`
  font-weight: bold;
`;

const NumericWrapper = styled.div`
  max-width: 100px;
`;

const DateRangeWrapper = styled.div`
  display: flex;
  margin-top: 8px;
`;

const FieldWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.metrics.margin + theme.metrics.tinyMargin}px;
`;

const SpotWrapper = styled(FieldWrapper)`
  display: flex;
  justify-content: space-between;
`;

const ErrorText = styled.div`
  margin-top: ${({ theme }) => theme.metrics.tinyMargin}px;
  font-size: ${({ theme }) => theme.fonts.style.xsmall.fontSize}px;
  color: ${({ theme }) => theme.colors.error};
`;

type Props = {
  theme: any;
  t: any;
  open: boolean;
  onRequestClose: () => any;

  visible: boolean;
  promotion: PromotionState;
  listOptions: FetchCampaignOptions;

  radio: Radio,
  token: string,
  actions: {
    createNewPromotion: (promotion: SalesPromotionParameters) => Promise<*>;
    openSnackbar: (kind: string, message: string) => any;
  };
  onClose: () => any;
};

type State = {
  campaigns: CampaignListing[],

  title: string;
  dateRange: { from: moment, to: moment };
  nbOfSpots: number;
  cost: number;
  discount: number;
  expirationDate: moment;
  hasAttemptedToSubmit: boolean;
  campaignReference: string;
};

const INITIAL_STATE = {
  campaigns: [],

  title: '',
  dateRange: { from: moment(), to: moment() },
  nbOfSpots: 0,
  cost: 0,
  discount: 0,
  expirationDate: moment().add(1, 'week'),
  hasAttemptedToSubmit: false,
  campaignReference: '',
};

class CreatePromotionDialog extends Component<Props, State> {
  constructor() {
    super();
    this.state = INITIAL_STATE;
    (this: any).onSubmit = this.onSubmit.bind(this);
  }

  componentDidMount() {
    const { radio, token } = this.props;
    CampaignAPI.listCampaigns(token, radio.callSign, FETCH_ALL_CAMPAIGNS)
      .then(campaigns => this.setState({ campaigns }));
  }

  onTitleChanged = (event: any, title: string) => this.setState({ title });

  onDateRangeChanged = dateRange => this.setState({ dateRange }, this.checkExpirationDateAfterEndDate);

  onNbOfSpotsChanged = nbOfSpots => this.setState({ nbOfSpots });

  onDiscountChanged = discount => this.setState({ discount });

  onExpirationDateChanged = (expirationDate) => {
    const now = moment();
    const validatedExpirationDate = expirationDate.isBefore(now) ? now : expirationDate;

    this.setState({ expirationDate: validatedExpirationDate }, this.checkExpirationDateAfterEndDate);
  }

  onCostChanged = cost => this.setState({ cost });

  onCampaignChanged = (campaignReference: string) => this.setState({ campaignReference });

  checkExpirationDateAfterEndDate = () => {
    const { expirationDate, dateRange } = this.state;

    if (expirationDate.isBefore(dateRange.to)) {
      this.setState({ expirationDate: dateRange.to });
    }
  }

  async onSubmit() {
    this.setState({ hasAttemptedToSubmit: true });
    if (!this.canSubmit()) {
      return;
    }

    const {
      title, nbOfSpots, cost, discount, campaignReference, expirationDate, dateRange,
    } = this.state;
    const discountInPercent = discount / 100;

    const salesPromotion = {
      cost,
      discount: discountInPercent,
      nbOfSpots,
      title,
      campaign: campaignReference,
      expirationDate: expirationDate.format(PARSED_DATE_FORMAT),
      dateRange: {
        from: dateRange.from.format(PARSED_DATE_FORMAT),
        to: dateRange.to.format(PARSED_DATE_FORMAT),
      },
    };

    await this.props.actions.createNewPromotion(salesPromotion);

    if (!this.props.promotion.creatingError) {
      this.props.onClose();
      this.props.actions.openSnackbar('message', this.props.t('promotionSent'));
    } else {
      this.props.actions.openSnackbar('error', this.props.t('promotionCreationError'));
    }
  }

  canSubmit = () => {
    const {
      title, nbOfSpots, cost, discount, campaignReference, expirationDate, dateRange,
    } = this.state;

    return Boolean(campaignReference) && Boolean(title) && cost > 0 && discount > 0 && nbOfSpots > 0 && Boolean(dateRange) && Boolean(expirationDate);
  }

  render() {
    const {
      theme, t, promotion,
    } = this.props;
    const {
      campaigns, hasAttemptedToSubmit, campaignReference, title, discount, cost, nbOfSpots,
    } = this.state;

    const titleErrorMessage = hasAttemptedToSubmit && !title ? t('missingTitleError') : '';
    const campaignErrorMessage = hasAttemptedToSubmit && !campaignReference ? t('missingCampaignError') : '';
    const discountErrorMessage = hasAttemptedToSubmit && discount <= 0 ? t('mustBeGreaterThanZero') : '';
    const costErrorMessage = hasAttemptedToSubmit && cost <= 0 ? t('mustBeGreaterThanZero') : '';
    const nbOfSpotsErrorMessage = hasAttemptedToSubmit && nbOfSpots <= 0 ? t('mustBeGreaterThanZero') : '';

    return (
      <Dialog
        autoScrollBodyContent
        title={t('newPromotion')}
        open={this.props.visible}
        onRequestClose={this.props.onClose}
        titleStyle={{ ...theme.fonts.style.secondary, color: theme.colors.tertiaryText, backgroundColor: theme.colors.primary }}
        bodyStyle={{ padding: 0 }}
        contentStyle={{ width: 600 }}
      >
        <Wrapper>
          <FieldWrapper>
            <StyledLabel>{t('title')}</StyledLabel>
            <TextField errorText={titleErrorMessage} id="promotion-title" value={this.state.title} onChange={this.onTitleChanged} />
          </FieldWrapper>

          <FieldWrapper>
            <FieldWrapper>
              <StyledLabel>{t('common:campaign')}</StyledLabel>
            </FieldWrapper>
            {campaigns &&
              <CampaignsDropdown
                value={campaignReference}
                campaigns={campaigns}
                onChange={this.onCampaignChanged}
              />
            }
            <ErrorText>{campaignErrorMessage}</ErrorText>
          </FieldWrapper>

          <FieldWrapper>
            <StyledLabel>{t('dateRange')}</StyledLabel>
            <DateRangeWrapper>
              <DateRangeIcon style={{ minWidth: 30, minHeight: 30 }} />
              <DateRangePicker
                minDate={this.props.radio.getCreatedAt()}
                from={this.state.dateRange.from}
                to={this.state.dateRange.to}
                onDateRangeChange={this.onDateRangeChanged}
                fontSize={14}
              />
            </DateRangeWrapper>
          </FieldWrapper>

          <SpotWrapper>
            <div>
              <StyledLabel>
                {t('nbOfSpots')}
              </StyledLabel>
              <NumericWrapper>
                <NumericTextField
                  value={this.state.nbOfSpots}
                  onBlur={this.onNbOfSpotsChanged}
                  errorText={nbOfSpotsErrorMessage}
                />
              </NumericWrapper>
            </div>
            <div>
              <StyledLabel>
                {t('cost')}
              </StyledLabel>
              <NumericWrapper>
                <MonetaryTextField
                  value={this.state.cost}
                  onBlur={this.onCostChanged}
                  errorText={costErrorMessage}
                />
              </NumericWrapper>
            </div>
            <div>
              <StyledLabel>
                {t('discount')}
              </StyledLabel>
              <NumericWrapper>
                <NumericTextField
                  value={this.state.discount}
                  onBlur={this.onDiscountChanged}
                  suffix="%"
                  errorText={discountErrorMessage}
                />
              </NumericWrapper>
            </div>
          </SpotWrapper>

          <FieldWrapper>
            <StyledLabel>{t('expirationDate')}</StyledLabel>
            <div>
              <DatePickerWithInput
                minDate={this.props.radio.getCreatedAt()}
                date={this.state.expirationDate}
                onDateChanged={this.onExpirationDateChanged}
              />
            </div>
          </FieldWrapper>
          <div>
            {t('expirationMessage', { expirationDate: this.state.expirationDate })}
          </div>
        </Wrapper>
        <DialogFooter>
          <RaisedButton
            key="cancelPromotion"
            label={t('cancel')}
            primary={false}
            onClick={this.props.onClose}
          />
          {!promotion.creating &&
            <RaisedButton
              key="submitPromotion"
              label={t('send')}
              primary
              onClick={this.onSubmit}
            />
          }
          {promotion.creating &&
            <LoadingSpinner size="small" />
          }
        </DialogFooter>
      </Dialog>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    token: getToken(state),
    radio: getCurrentRadio(state),
    promotion: getPromotionState(state),
  };
};

function mapDispatchToProps(dispatch: *): Object {
  return {
    actions: bindActionCreators({ createNewPromotion, openSnackbar }, dispatch),
  };
}

export default compose(translate('promotion'), connect(mapStateToProps, mapDispatchToProps), withTheme)(CreatePromotionDialog);



// WEBPACK FOOTER //
// ./src/statsradio/modules/promotions/CreatePromotionDialog.js